import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import maplibregl from "maplibre-gl";

const LIBRE_GLB_TASIK = ({ layer, properties }) => {
  const { map_object } = layer;
  const customLayerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!map_object) return;
    const modelOrigin = [108.22145, -7.32141];
    const modelAltitude = 0;
    const modelRotate = [Math.PI / 2, -Math.PI / 3.3, 0]; // Y-axis rotation -45 degrees

    const flyToCenter = [108.2237202904696, -7.322424611305763];

    const modelAsMercatorCoordinate = maplibregl.MercatorCoordinate.fromLngLat(
      modelOrigin,
      modelAltitude
    );

    const modelTransform = {
      translateX: modelAsMercatorCoordinate.x,
      translateY: modelAsMercatorCoordinate.y,
      translateZ: modelAsMercatorCoordinate.z,
      rotateX: modelRotate[0],
      rotateY: modelRotate[1],
      rotateZ: modelRotate[2],
      scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits(),
    };

    const customLayer = {
      id: "3d-station",
      type: "custom",
      renderingMode: "3d",
      onAdd: function (map, gl) {
        const camera = new THREE.Camera();
        const scene = new THREE.Scene();

        // Store in refs for external access
        cameraRef.current = camera;
        sceneRef.current = scene;
        mapRef.current = map;

        // Enhanced lighting with pastel colors and higher intensity

        // Soft ambient light for overall brightness
        const ambientLight = new THREE.AmbientLight(0xf8f8ff, 0.4);
        scene.add(ambientLight);

        // Pastel directional lights with higher intensity
        const light1 = new THREE.DirectionalLight(0xffe6f2, 1.8); // Bright soft pink
        light1.position.set(0, -70, 100); // Removed normalize() for proper positioning
        scene.add(light1);

        const light2 = new THREE.DirectionalLight(0xe6f3ff, 1.8); // Bright soft blue
        light2.position.set(0, 70, 100); // Removed normalize() for proper positioning
        scene.add(light2);

        // Additional soft side lighting for better model illumination
        const light3 = new THREE.DirectionalLight(0xfff2e6, 1.2); // Soft peach
        light3.position.set(100, 0, 50);
        scene.add(light3);

        // load GLB model
        const loader = new GLTFLoader();
        loader.load(
          "https://maps-icons.s3.ap-southeast-3.amazonaws.com/3d-model/3D+STASIUN+TASIKMALAYA.glb",
          (gltf) => {
            // // Make the 3D model 50% transparent
            // gltf.scene.traverse((child) => {
            //   if (child.isMesh) {
            //     // Enable transparency
            //     child.material.transparent = true;
            //     child.material.opacity = 1; //0.5 --> semi transparent

            //     // If it's an array of materials (multi-material mesh)
            //     if (Array.isArray(child.material)) {
            //       child.material.forEach((material) => {
            //         material.transparent = true;
            //         material.opacity = 1; //0.5 --> semi transparent
            //       });
            //     }
            //   }
            // });

            scene.add(gltf.scene);
          },
          undefined,
          (error) => {
            console.error("Error loading GLB:", error);
          }
        );

        const renderer = new THREE.WebGLRenderer({
          canvas: map.getCanvas(),
          context: gl,
          antialias: true,
        });
        renderer.autoClear = false;

        // Store in ref for external access
        rendererRef.current = renderer;

        // Store references in the layer object for MapLibre's use
        this.camera = camera;
        this.scene = scene;
        this.renderer = renderer;
        this.map = map;
      },
      on_render: function () {
        // Re-render the scene using refs when possible
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const map = mapRef.current;

        if (renderer && scene && camera && map) {
          renderer.resetState();
          renderer.render(scene, camera);
          map.triggerRepaint();
        }
      },
      render: function (gl, matrix) {
        const rotationX = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(1, 0, 0),
          modelTransform.rotateX
        );
        const rotationY = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 1, 0),
          modelTransform.rotateY
        );
        const rotationZ = new THREE.Matrix4().makeRotationAxis(
          new THREE.Vector3(0, 0, 1),
          modelTransform.rotateZ
        );

        const m = new THREE.Matrix4().fromArray(
          matrix.defaultProjectionData.mainMatrix
        );
        const l = new THREE.Matrix4()
          .makeTranslation(
            modelTransform.translateX,
            modelTransform.translateY,
            modelTransform.translateZ
          )
          .scale(
            new THREE.Vector3(
              modelTransform.scale,
              -modelTransform.scale,
              modelTransform.scale
            )
          )
          .multiply(rotationX)
          .multiply(rotationY)
          .multiply(rotationZ);

        this.camera.projectionMatrix = m.multiply(l);
        this.renderer.resetState();
        this.renderer.render(this.scene, this.camera);
        this.map.triggerRepaint();
      },
    };

    const add3DLayer = () => {
      if (!map_object.getLayer("3d-station")) {
        // Add layer after all base layers to ensure it's on top
        // const layers = map_object.getStyle().layers;
        // const firstSymbolLayerId = layers.find(
        //   (layer) => layer.type === "symbol"
        // )?.id;

        map_object.addLayer(
          customLayer
          // , firstSymbolLayerId
        );
        customLayerRef.current = customLayer; // Store reference

        map_object.flyTo({
          center: flyToCenter,
          zoom: 20,
          pitch: 60,
          speed: 2,
          curve: 1.5,
          easing: (t) => t,
        });
      }
    };

    if (map_object.isStyleLoaded()) {
      add3DLayer();
    } else {
      map_object.once("style.load", add3DLayer);
    }

    return () => {
      if (map_object.getLayer("3d-station")) {
        map_object.removeLayer("3d-station");
      }
      customLayerRef.current = null;
    };
  }, [map_object]);

  useEffect(() => {
    if (!map_object || !properties?.basemap_used) return;

    const layer = customLayerRef.current;
    if (layer) {
      // Remove and re-add the layer to ensure it stays on top
      if (map_object.getLayer("3d-station")) {
        map_object.removeLayer("3d-station");
      }

      // Wait for style to load, then re-add the layer
      const readdLayer = () => {
        if (!map_object.getLayer("3d-station")) {
          map_object.addLayer(layer);
          if (layer.on_render) {
            layer.on_render();
          }
        }
      };

      if (map_object.isStyleLoaded()) {
        readdLayer();
      } else {
        map_object.once("style.load", readdLayer);
      }
    }
  }, [map_object, properties?.basemap_used]);

  return null;
};

const mapStateToProps = (state) => ({
  layer: state.layer,
  properties: state.properties, // Add properties to props
});

export default connect(mapStateToProps)(LIBRE_GLB_TASIK);
